import {
  MeetingSessionConfiguration,
  MeetingSessionStatusCode,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  BackgroundBlurVideoFrameProcessor,
  DefaultVideoTransformDevice,
  DefaultEventController,
  DefaultModality 
} from 'amazon-chime-sdk-js';
import UAParser from 'ua-parser-js';
import axios from 'axios';
import logger from '@/modules/Logger';
import store from '@/store/index';
import { getDeviceAccessInstance } from '@/utils/getDeviceAccess';
import { getManualMicId } from '@/modules/webrtc/getManualMicId';
import { myStageBackendBaseUrl } from '@/utils/constants';
import {
  RECONNECT_RECOVERY_PING_INTERVAL_MS,
  RECONNECT_RECOVERY_PING_TIMEOUT_MS
} from '@/modules/webrtc/agora/const';

// Longer than the SDK's unusable-audio sample window (15 samples, 1 per second)
const RECONNECT_GRACE_MS = 20000;

/**
 * Amazon Chime's adapter for client
 */
class ChimeAdapter {
  constructor(Option) {
    this.options = Option;
    this.peerId = this.options.credential.attendees.ExternalUserId
    this.remoteUsers = Option.remoteUsers;
    this.videoEffect = Option.videoEffect;
    this.videoProfile = Option.videoProfile;
    this.remoteUserId = Option.remoteExternalId;
    (this.enableAudio = Option.enableAudio),
    (this.enableVideo = Option.enableVideo),
    (this.constraints = {
      audio: {
        'deviceId': Option.audioDevice
      },
      video: {
        'deviceId': Option.videoDevice
      }
    });
    this.videoDevice = Option.videoDevice || null;
    this.audioInputDevice = Option.audioDevice || null;
    this.availableDevices = null;
    
    this.logger = null;
    this.stream = null;
    this.audioVideo = null;
    this.meetingEvent = null;
    this.meetingSession = null;
    this.isScreenShare = false;
    this.audioOutputDevice = Option.audioOutputDevice || null;

    this.videoQualityProfiles = {
      poor: {
        resolution: '180p',
        width: 320,
        height: 180,
        frameRate: 15,
        maxBandwidthKbps: 500
      },
      excellent: {
        resolution: '360p',
        width: this.videoProfile.width,
        height: this.videoProfile.height,
        frameRate: this.videoProfile.frameRate,
        maxBandwidthKbps: this.videoProfile.maxBandwidthKbps
      }
    };

    this.currentVideoQualityProfile = this.videoQualityProfiles.excellent;
    this.metricsObserver = null;
    this.bufferSwitchMinCount = 30;
    this.bufferSwitchCounter = 0;
    this.lessonId = Option.lessonId;
    this.sessionStarted = false;
    this.wasReconnecting = false;
    this.sdkReconnecting = false;
    this.reconnectedAt = 0;
    // One-shot observer for restarting video after screen share stops; tracked
    // so it can be removed when it fires and disabled on disconnect()/reset().
    this._republishObserver = null;

    // Browser-level network detection for instant reconnection overlay
    this._onOffline = () => {
      if (this.sessionStarted) {
        this.wasReconnecting = true;
        logger.info('[AWS Chime] Browser offline event detected', { operation: 'webrtc:connection', event_type: 45, lesson_id: this.lessonId });
        store.commit('connection/setReconnecting', true);
        // Pings would only fail while offline; the next `online` event restarts the poll.
        this.stopReconnectRecoveryPoll();
      }
    };
    this._onOnline = () => {
      if (this.sessionStarted) {
        logger.info('[AWS Chime] Browser online event detected', { operation: 'webrtc:connection', lesson_id: this.lessonId });
        // The `online` event is NOT a reliable signal of real connectivity: on
        // multi-homed machines (VPN, secondary NIC, tethering) it can fire after
        // Wi-Fi is turned off. Only clear the overlay once the backend is actually
        // reachable. If audioVideoDidStart fires first (SDK reconnected), it clears
        // the overlay immediately and the poll stops itself.
        this.startReconnectRecoveryPoll();
      }
    };
    this._reconnectRecoveryTimer = null;
    window.addEventListener('offline', this._onOffline);
    window.addEventListener('online', this._onOnline);

    this.setConfiguration();
  }

  setConfiguration() {
    this.configuration = new MeetingSessionConfiguration(
      {
        Meeting: {
          MeetingId: this.options.credential.meeting.MeetingId,
          MediaPlacement: {
            AudioHostUrl: this.options.credential.meeting.MediaPlacement.AudioHostUrl,
            ScreenDataUrl: this.options.credential.meeting.MediaPlacement.ScreenDataUrl,
            ScreenSharingUrl: this.options.credential.meeting.MediaPlacement.ScreenSharingUrl,
            ScreenViewingUrl: this.options.credential.meeting.MediaPlacement.ScreenViewingUrl,
            SignalingUrl: this.options.credential.meeting.MediaPlacement.SignalingUrl,
            TurnControlUrl: this.options.credential.meeting.MediaPlacement.TurnControlUrl,
          },
        },
      },
      {
        Attendee: {
          ExternalUserId: this.peerId,
          AttendeeId: this.options.credential.attendees.AttendeeId,
          JoinToken: this.options.credential.attendees.JoinToken,
        },
      }
    );

    this.configuration.enableWebAudio = true;
    return this.configuration;
  }

  initConsoleLogger() {
    this.logger = new ConsoleLogger('SDK', LogLevel.ERROR);
  }

  initDeviceController() {
    if (!this.logger) {
      return null;
    }

    return new DefaultDeviceController(this.logger);
  }

  lifeCycleCallbacks() {
    this.meetingEvent.addObserver({
      eventDidReceive: async (name, attributes) => {
        const { meetingHistory, ...otherAttributes } = attributes;

        // filters the last 5minute logs
        const recentMeetingHistory = meetingHistory.filter(({ timestampMs }) => {
          return Date.now() - timestampMs < 5 * 60 * 1000;
        });
      
        switch (name) {
          case 'meetingFailed':
          case 'audioInputFailed':
          case 'videoInputFailed':
          case 'meetingStartFailed':
            logger.error(`Failure: ${name} with attributes: `, {
              ...otherAttributes,
              recentMeetingHistory,event_type:68,lesson_id:this.lessonId
            });
            break;
          case 'signalingDropped':
            logger.error(`Failure: ${name} with attributes: `, {
              ...otherAttributes,
              recentMeetingHistory,event_type:69,lesson_id:this.lessonId
            });
            break;
          default:
            logger.info(`Meeting event observer: ${name}, ${JSON.stringify(attributes)}`);
        }
      }
    });

    this.audioVideo.addContentShareObserver({
      contentShareDidStop: async () => {
        store.commit('videoRtc/setChimeShareScreenDidStop', true);
      }
    });

    this.audioVideo.addObserver({
      videoTileDidUpdate: tileState => {
        // Ignore a tile without attendee ID and other attendee's tile.
        if (!tileState.boundAttendeeId) {
          return;
        }
        this.updateTiles();
      },
      videoTileWasRemoved: tileState => {
        if (!tileState) {
          return;
        }
        this.updateTiles();
      },
      audioVideoDidStop: sessionStatus => {
        const statusCode = sessionStatus.statusCode();

        // Only clear reconnecting if this is NOT an intentional disconnect (manual reconnect).
        // When the user clicks Reconnect, `refreshed` is set to true before the component
        // tears down — in that case the parent already set isReconnecting=true and we must
        // not undo it here.
        if (!store.state.connection.refreshed) {
          store.commit('connection/setReconnecting', false);
        }

        switch (statusCode) {
          case MeetingSessionStatusCode.AudioJoinedFromAnotherDevice:
            window.location.reload();
            break;
        }
      },
      // Metrics monitoring and adjusting of stream quality.
      metricsDidReceive: async (clientMetricReport) => {
        this.bufferSwitchCounter += 1;
        if (this.bufferSwitchCounter >= this.bufferSwitchMinCount) {
          await this.adjustStreamQuality(clientMetricReport);
          this.bufferSwitchCounter = 0;
        }
      },
      // Indicates that the audio connection is experiencing packet loss.
      connectionDidSuggestStopVideo: () => {
        // A full network drop also triggers this. Recovery is handled by the reconnect flow,
        // pausing here would leave the remote tile paused after the session comes back.
        // The SDK measures audio loss over the last 15 seconds, so it still fires shortly after
        // reconnecting because the window includes the outage. Ignore it during that grace period.
        const withinReconnectGrace = Date.now() - this.reconnectedAt < RECONNECT_GRACE_MS;
        if (!navigator.onLine || this.wasReconnecting || withinReconnectGrace) {
          logger.info('[AWS Chime] connectionDidSuggestStopVideo ignored while reconnecting');
          return;
        }
        logger.info('[AWS Chime] connectionDidSuggestStopVideo fallback, turning remote video off');
        this.toggleRemoteVideo(true);
      },
      // Indicates that the audio connection has improved.
      connectionDidBecomeGood:() => {
        logger.info('[AWS Chime] connectionDidBecomeGood fallback, turning remote video on');
        this.toggleRemoteVideo(false);
      },
      audioVideoDidStart: () => {
        store.commit('connection/setHasConnectedOnce');
        store.commit('connection/setReconnecting', false);

        this.sdkReconnecting = false;
        if (this.wasReconnecting) {
          this.restoreVideoAfterReconnect();
        }
      },
      audioVideoDidStartConnecting: (reconnecting) => {
        if (reconnecting) {
          this.wasReconnecting = true;
          this.sdkReconnecting = true;
          this.removeStaleRemoteTiles();
          store.commit('connection/setReconnecting', true);
        }
      },
    });
  }

  async join() {
    let stream = null;
    let rtc = this.audioVideo;

    await this.setDeviceAvailability();

    // if both are false, then bypass stream creation
    if (this.constraints.video || this.constraints.audio) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(this.constraints);
      } catch (err) {
        logger.error('[AWS Chime] Error on join', {
          operation: 'webrtc:join',
          payload: err.message || err.name,
          event_type:70,lesson_id:this.lessonId,
          constraint: this.constraints,
        });
      }
    }

    try {
      await this.initializeMeetingSession();
    } catch (err) {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      throw err;
    }

    this.stream = stream;

    return { peerId: this.peerId, stream, rtc };
  }

  /**
   * Start a self-cancelling poll that keeps the reconnecting overlay up until
   * the MSB backend is actually reachable. Called on the browser `online` event.
   * Only a successful ping clears the overlay; failures keep it visible and
   * reschedule the next check.
   */
  startReconnectRecoveryPoll() {
    // Never run more than one loop at a time.
    this.stopReconnectRecoveryPoll();
    logger.info('[AWS Chime][RecoveryPoll] Poll started — will re-check backend reachability every ' + RECONNECT_RECOVERY_PING_INTERVAL_MS + 'ms', { operation: 'webrtc:connection', lesson_id: this.lessonId });

    const tick = async () => {
      // Self-terminate if the overlay was already cleared by another path
      // (e.g. audioVideoDidStart after the SDK reconnected).
      if (!store.state.connection.isReconnecting) {
        logger.info('[AWS Chime][RecoveryPoll] Tick skipped — isReconnecting already false, stopping poll', { operation: 'webrtc:connection', lesson_id: this.lessonId });
        this._reconnectRecoveryTimer = null;
        return;
      }

      const reachable = await this.isBackendReachable();
      logger.info('[AWS Chime][RecoveryPoll] Tick result — backend reachable', { reachable, operation: 'webrtc:connection', lesson_id: this.lessonId });

      // Re-check the flag: it may have changed while awaiting the ping.
      if (!store.state.connection.isReconnecting) {
        this._reconnectRecoveryTimer = null;
        return;
      }

      if (reachable) {
        logger.info('[AWS Chime] Backend reachable after online event — clearing reconnecting overlay', {
          operation: 'webrtc:connection',
          lesson_id: this.lessonId
        });
        this._reconnectRecoveryTimer = null;
        store.commit('connection/setReconnecting', false);
        return;
      }

      // Still unreachable — keep the overlay up and check again shortly.
      this._reconnectRecoveryTimer = setTimeout(tick, RECONNECT_RECOVERY_PING_INTERVAL_MS);
    };

    this._reconnectRecoveryTimer = setTimeout(tick, RECONNECT_RECOVERY_PING_INTERVAL_MS);
  }

  /**
   * Cancel any pending reconnect-recovery poll. Safe to call multiple times.
   */
  stopReconnectRecoveryPoll() {
    if (this._reconnectRecoveryTimer) {
      logger.info('[AWS Chime][RecoveryPoll] Poll cancelled — clearing pending timer', { operation: 'webrtc:connection', lesson_id: this.lessonId });
      clearTimeout(this._reconnectRecoveryTimer);
      this._reconnectRecoveryTimer = null;
    }
  }

  /**
   * Resolve true if the MyStage backend (MSB) host responds at all, false on a
   * network failure or timeout. Same probe as the Agora adapter: any HTTP status
   * from MSB /graphql counts as reachable.
   *
   * @return {Promise<boolean>}
   */
  async isBackendReachable() {
    // If MSB base URL isn't configured, we can't probe it — fall back to
    // navigator.onLine so we don't hang the overlay forever.
    if (!myStageBackendBaseUrl) {
      return navigator.onLine;
    }

    let timeoutId = null;
    const timeout = new Promise(resolve => {
      timeoutId = setTimeout(() => resolve(false), RECONNECT_RECOVERY_PING_TIMEOUT_MS);
    });

    const probe = axios
      .get(myStageBackendBaseUrl + '/graphql', {
        timeout: RECONNECT_RECOVERY_PING_TIMEOUT_MS,
        // Any HTTP status counts as a successful reach; don't throw on 4xx/5xx.
        validateStatus: () => true
      })
      .then(() => true)
      .catch(() => false);

    try {
      return await Promise.race([probe, timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async reset() {
    window.removeEventListener('offline', this._onOffline);
    window.removeEventListener('online', this._onOnline);
    this.stopReconnectRecoveryPoll();

    // Disable any pending screen-share restart so it does not fire on this stop.
    this.disableRepublishObserver();

    try {
      if (this.audioVideo) {
        await this.audioVideo.stopVideoInput();
      }
    } catch (e) {
      logger.error('[AWS Chime] Error stopping video input during reset', {
        operation: 'webrtc:stopVideoInput',
        error: e.message || e
      });
    }

    try {
      if (this.audioVideo) {
        this.audioVideo.stop();
      }
    } catch (e) {
      logger.debug('[AWS Chime] Error stopping audioVideo during reset', {
        operation: 'webrtc:reset',
        reason: e.message,
      });
    }

    this._releaseAllTracks();
    this.stream = null;
  }

  isNetworkError(error) {
    const retryableCodes = new Set([
      MeetingSessionStatusCode.SignalingInternalServerError,
      MeetingSessionStatusCode.SignalingRequestFailed,
      MeetingSessionStatusCode.TaskFailed,
    ]);
    return typeof error.code === 'number' && retryableCodes.has(error.code);
  }

  async trackRealtimePeerPresence() {
    this.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      async (presentAttendeeId, present, externalUserId) => {
        logger.info(`Attendee ID: ${presentAttendeeId} Present: ${present} ExternalUserId: ${externalUserId} PeerId: ${this.peerId}`, { operation: 'webrtc:realtimeSubscribeToAttendeeIdPresence' });

        if (externalUserId.includes('MediaPipeline')) externalUserId = this.peerId;

        const baseAttendeeId = new DefaultModality(presentAttendeeId).base();

        if (this.remoteUsers.length > 0) this.remoteUserId = externalUserId;

        if (this.isScreenShare || baseAttendeeId !== presentAttendeeId) {
          return;
        }

        if (externalUserId == this.remoteUserId) {
          if (present && !this.isRemoteUserExist(this.peerId)) {
            if (this.remoteUsers.length === 0) {
              this.remoteUsers.push({ peerId: this.remoteUserId, stream: this.getPeerStream() });
              logger.info('[AWS Chime] remote user join: ' + this.remoteUserId);
            }
          } else {
            for (let idx in this.remoteUsers) this.remoteUsers.splice(idx, 1);
            logger.info('[AWS Chime] remote user leave: ' + this.remoteUserId);
          }
        }

        this.rebindVideoTile();
      }
    );
  }

  async getAudioInputDevice(deviceId) {
    const mics = await this.audioVideo.listAudioInputDevices(true);
    let device = mics.find(mic => mic.deviceId === deviceId);
    if (!device && mics.length) {
      device = mics[0];
    }

    return device;
  }

  async getVideoDevice(deviceId) {
    const cameras = await this.audioVideo.listVideoInputDevices(true);
    console.log([
      'Get all video devices',
      cameras
    ]);
    let device = cameras.find(cam => cam.deviceId === deviceId);
    if (!device && cameras.length) {
      device = cameras[0];
    }

    console.log([
      'Get newly selected video device to set',
      device
    ]);

    return device;
  }

  async getAudioOutputDevice(deviceId) {
    const speakers = await this.audioVideo.listAudioOutputDevices(true);
    let device = speakers.find(speaker => speaker.deviceId === deviceId);
    if (!device && speakers.length) {
      device = speakers[0];
    }

    return device;
  }

  async setAudioDevice(deviceId) {
    const oldValue = this.audioInputDevice;
    this.audioInputDevice = deviceId;
    let newAudioInput = null;

    if (this.isScreenShare) {
      return;
    }

    // set audio input to null if there's an error with mic
    if (store.getters['devices/hasMicError']) {
      this.audioVideo.startAudioInput(null);

      logger.error('[AWS Chime] Unable to switch to the selected audio device', {
        operation: 'webrtc:setAudioDevice',
        reason: store.state.devices.micDeviceError
      });

      return { error: true };
    }

    try {
      const newlySetDevice = await this.getAudioInputDevice(this.audioInputDevice);
      newAudioInput = await this.audioVideo.startAudioInput(newlySetDevice.deviceId);
      logger.info(`[AWS Chime] Setting new Audio Input device  ${JSON.stringify(newlySetDevice)}`, {
        operation: 'webrtc:setAudioDevice',
      });
    } catch (e) {
      newAudioInput = { error: true };
      const previousSetDevice = await this.getAudioInputDevice(oldValue);
      await this.audioVideo.startAudioInput(previousSetDevice.deviceId);
      logger.error('[AWS Chime] Unable to switch to the selected audio device', {
        operation: 'webrtc:setAudioDevice',
        reason: e.message || e,event_type:71,lesson_id:this.lessonId
      });
    }

    const audioElement = document.getElementById('meeting-audio');
    if (audioElement) {
      this.audioVideo.bindAudioElement(audioElement);
    }

    return newAudioInput;
  }

  async setAudioOutputDevice(deviceId) {
    const oldValue = this.audioOutputDevice;
    this.audioOutputDevice = deviceId;
    logger.info(`Requested Audio Output device ${deviceId}`);

    if (this.isScreenShare) {
      return;
    }

    try {
      const newlySetDevice = await this.getAudioOutputDevice(this.audioOutputDevice);
      if (newlySetDevice === undefined) {
        logger.error('getAudioOutputDevice is undefined on newlySetDevice', {
          operation: 'webrtc:setAudioOutputDevice',event_type:72,lesson_id:this.lessonId
        });
        throw new Error('getAudioOutputDevice is undefined on newlySetDevice');
      }

      await this.audioVideo.chooseAudioOutput(newlySetDevice.deviceId);
      logger.info(`[AWS Chime] Setting new Audio Output device ${JSON.stringify(newlySetDevice)}`, {
        operation: 'webrtc:setAudioOutputDevice',
      });
    } catch (e) {
      if (oldValue) {
        const previousSetDevice = await this.getAudioOutputDevice(oldValue);
        if (previousSetDevice !== undefined) {
          await this.audioVideo.chooseAudioOutput(previousSetDevice.deviceId);
          logger.error('[AWS Chime] Failed to set new Audio Output device.', {
            operation: 'webrtc:setAudioOutputDevice',
            reason: e.message || e,event_type:73,lesson_id:this.lessonId
          });
        } else {
          logger.error('getAudioOutputDevice is undefined on previousSetDevice', {
            operation: 'webrtc:setAudioOutputDevice',event_type:75,lesson_id:this.lessonId
          });
        }
      }
    }
  }

  async setVideoDevice(deviceId) {
    this.videoDevice = deviceId;
    let newVideoInput = null

    if (this.isScreenShare) {
      return;
    }

    console.log([
      'Set video device',
      this.videoDevice
    ]);

    await getDeviceAccessInstance.getCameraDevice(this.videoDevice);

    try {
      const newlySetDevice = await this.getVideoDevice(this.videoDevice);

      newVideoInput = await this.audioVideo.startVideoInput(newlySetDevice.deviceId)
      await this.setBlurWhenInActiveState();
      
      this.audioVideo.startLocalVideoTile();

      // Only call start() if the session is already running (mid-lesson device switch).
      // During initializeMeetingSession(), start() is called separately after observer setup.
      if (this.sessionStarted) {
        this.audioVideo.start();
      }

      logger.info(`[AWS Chime] Setting new Video Input device ${JSON.stringify(newlySetDevice)}`, {
        operation: 'webrtc:setVideoInputDevice',
      });
    } catch (e) {
      newVideoInput = { error: true };
      logger.info(
        '[AWS Chime] Failed to set new  Video Input device',
        { operation: 'webrtc:setVideoInputDevice', reason: e.message || e }
      );
    }
    return newVideoInput;
  }

  async setVideoQuality(videoQualityProfile) {
    // Checker if video quality changed
    const { previousWidth, previousHeight } = await this.getLocalVideoTileDimensions();
    if (previousWidth !== null && previousHeight !== null) {
        logger.info(`[AWS Chime] Local video previous width: ${previousWidth}, height: ${previousHeight}`);
    }

    logger.info(`[AWS Chime] Setting video quality to ${JSON.stringify(videoQualityProfile)}`);
    await this.audioVideo.chooseVideoInputQuality(
      videoQualityProfile.width,
      videoQualityProfile.height,
      videoQualityProfile.frameRate
    );
    await this.audioVideo.setVideoMaxBandwidthKbps(videoQualityProfile.maxBandwidthKbps);
  }

  async initializeMeetingSession() {
    this.setConfiguration();
    this.initConsoleLogger();
    this.meetingEvent = new DefaultEventController(
      this.configuration,
      this.logger
    );
    this.meetingSession = new DefaultMeetingSession(
      this.configuration,
      this.logger,
      this.initDeviceController(),
      this.meetingEvent
    );
    this.audioVideo = this.meetingSession.audioVideo;
    this.lifeCycleCallbacks();

    this.audioVideo.setDeviceLabelTrigger(async () => {
      let constraints = { 
        audio: this.constraints.audio, 
        video: this.constraints.video 
      };

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        return stream; 
      } catch (error) {
        logger.debug('[awschime] Error on getting device label', { 
          operation: 'webrtc:setDeviceLabelTrigger', 
          payload: error.message || error,
          constraints
        });
        this.switchToFlow('flow-devices'); 
        return null; 
      }
    });

    // Set up devices
    if (this.audioInputDevice) await this.setAudioDevice(this.audioInputDevice);
    await this.setAudioOutputDevice(this.audioOutputDevice);
    await this.setVideoQuality(this.videoQualityProfiles.excellent);
    await this.setVideoDevice(this.videoDevice);

    // Start sending video
    this.audioVideo.startLocalVideoTile();

    await this.trackRealtimePeerPresence();

    // Apply initial audio state before starting the session
    this.toggleMic(this.enableAudio);

    // Start meeting session and wait for connection confirmation
    await new Promise((resolve, reject) => {
      let settled = false;

      this.audioVideo.addObserver({
        audioVideoDidStart: () => {
          if (!settled) {
            settled = true;
            this.sessionStarted = true;
            store.commit('connection/setHasConnectedOnce');
            store.commit('connection/setReconnecting', false);
            logger.info('[AWS Chime] audioVideoDidStart - connection established', { operation: 'webrtc:join' });
            resolve();
          }
        },
        audioVideoDidStop: (sessionStatus) => {
          if (!settled) {
            settled = true;
            const statusCode = sessionStatus.statusCode();
            logger.error('[AWS Chime] audioVideoDidStop before start - connection failed', {
              operation: 'webrtc:join',
              statusCode,
              event_type: 70,
              lesson_id: this.lessonId,
            });
            reject({ message: `Chime connection failed with status code: ${statusCode}`, code: statusCode });
          }
        },
      });

      this.audioVideo.start();
    });
  }

  async adjustStreamQuality(clientMetricReport) {
    if (store.state.videoRtc.audioOnly) {
      return;
    }
    // Extract metrics from metricReport
    const {
      videoUpstreamBitrate,
      availableOutgoingBitrate
    } = clientMetricReport.getObservableMetrics();

    const outgoingVideoBandwidthKbps = videoUpstreamBitrate ? videoUpstreamBitrate / 1000 : 0;
    const outgoingBandwidthKbps = availableOutgoingBitrate ? availableOutgoingBitrate / 1000 : 0;
    const outgoingBandwidthKbpsToUse = outgoingVideoBandwidthKbps || outgoingBandwidthKbps;
    logger.info(`[AWS Chime] Outgoing video bandwidth: ${outgoingVideoBandwidthKbps.toFixed(2)} kbps, outgoing bandwidth: ${outgoingBandwidthKbps.toFixed(2)} kbps`);

    // Check if video quality needs to be adjusted according to the available outgoing bandwidth
    let selectedProfile = this.getSelectedVideoQualityProfile(outgoingBandwidthKbpsToUse);
    if (selectedProfile !== this.currentVideoQualityProfile) {
      // add transition to avoid flickering when changing video quality
      const videoElement = document.getElementById('meeting-video-' + store.state.localExternalId);
      if (videoElement) {
        videoElement.style.opacity = 0;
      }

      await this.setVideoQuality(selectedProfile);

      const transitionTimeout = this.videoEffect === 'blur' ? 0 : 250;
      setTimeout(async () => await this.setVideoDevice(this.videoDevice), transitionTimeout);

      this.currentVideoQualityProfile = selectedProfile;
    }
  }

  getSelectedVideoQualityProfile(outgoingBandwidthKbps) {
    return Object.values(this.videoQualityProfiles)
      .find(profile => outgoingBandwidthKbps < profile.maxBandwidthKbps) || this.videoQualityProfiles.excellent;
  }

  async getLocalVideoTileDimensions() {
    const localTile = this.audioVideo.getLocalVideoTile();
    if (localTile) {
        const { videoStreamContentWidth: previousWidth, videoStreamContentHeight: previousHeight } = localTile.tileState;
        return { previousWidth, previousHeight };
    }
    return { previousWidth: null, previousHeight: null };
  }

  updateTiles() {
    const tiles = this.audioVideo.getAllVideoTiles();
    tiles.forEach(tile => {
      let tileId = tile.tileState.tileId;
      let externalUserId = tile.tileState.boundExternalUserId;
      const videoElement = document.getElementById('meeting-video-' + externalUserId);
      if (videoElement) {
        this.audioVideo.bindVideoElement(tileId, videoElement);
        setTimeout(() => videoElement.style.opacity = 1, 250);
      }
    });
  }

  // set video & audio constraints based on the device availability
  setDeviceAvailability() {    
    return new Promise(async (resolve) => {
      // Safari, Firefox device IDs are only available after the user has granted permission
      await getDeviceAccessInstance.initiateDeviceRequest(this.videoDevice, this.audioInputDevice);

      navigator.mediaDevices.enumerateDevices().then(devices => {
        const cameras = devices.filter(device => this.filterDevice('videoinput', device));
        const mics = devices.filter(device => device.kind === 'audioinput');
        const speakers = devices.filter(device => device.kind === 'audiooutput');

        if (!cameras.length || store.getters['devices/hasCameraError']) {
          this.enableVideo = false;
          this.constraints.video = false;
        }

        if (!mics.length || store.getters['devices/hasMicError']) {
          this.enableAudio = false;
          this.constraints.audio = false;
        }

        this.devicesListHasCurrentDeviceId(cameras, mics);

        // add audio output in devices list
        this.availableDevices = { cameras, mics, speakers };

        logger.info('Detected media devices', { operation: 'devices:list', payload: this.availableDevices });
        resolve({ cameras, mics });
      });
    });
  }

  async shareScreen(mode) {
    try {
      if (mode) {
        const stream = new MediaStream();
        const screenStream = await this.audioVideo.startContentShareFromScreenCapture();

        if (screenStream) {
          if (this.stream) {
            this.stream.getAudioTracks().forEach(audio => {
              if (!this.enableAudio) audio.enabled = false;
              stream.addTrack(audio);
            })
          }

          const videoTracks = screenStream.getVideoTracks();
          videoTracks.forEach(video => {
            // Favor frame rate over resolution so full-motion tab shares (e.g. a
            // playing video) do not collapse to a white frame on the remote side.
            // Mac Chrome tab capture otherwise defaults toward a "detail" hint.
            if ('contentHint' in video) {
              video.contentHint = 'motion';
            }
            stream.addTrack(video);
          });

          if (videoTracks.length > 0) {
            logger.info('[AWS Chime] Applied motion content hint to shared video tracks', {
              operation: 'webrtc:shareScreen',
              track_count: videoTracks.length,
              lesson_id: this.lessonId
            });
          }

          // Include captured system audio if present
          screenStream.getAudioTracks().forEach(audio => stream.addTrack(audio));

          const hasScreenAudio = screenStream.getAudioTracks().length > 0;
          logger.info(
            hasScreenAudio
              ? '[awschime] Screen share started with audio track'
              : '[awschime] Screen share started without audio track',
            { operation: 'webrtc:share-screen', lesson_id: this.lessonId }
          );

          this.isScreenShare = true;
        }

        return { stream }
      }

      // End screen share
      this.audioVideo.stopContentShare();
      if (!store.state.lesson.isLessonHasEnded) {
        await this.republishVideoDevice();
      }
      this.isScreenShare = false;

      store.commit('videoRtc/setChimeShareScreenDidStop', false);
      return { stream: this.stream }
    } catch (error) {
      logger.error('[awschime] Error on shareScreen', { operation: 'webrtc:shareScreen', payload: error.message || error,event_type:76,lesson_id:this.lessonId });
      return { error }
    }
  }

  // Restart video after screen share stops: wait for audioVideoDidStop before
  // start() (else it races teardown and fails with SignalChannelClosedUnexpectedly).
  async republishVideoDevice() {
    this.disableRepublishObserver();

    this._republishObserver = {
      audioVideoDidStop: async () => {
        // One-shot: detach immediately so it never fires on a later stop.
        this.disableRepublishObserver();
        
        if (!store.state.lesson.isLessonHasEnded) {
          await this.setVideoDevice(this.videoDevice);
          await this.setVideoQuality(this.videoQualityProfiles.excellent);
          this.audioVideo.start();
        }
      }
    };

    this.audioVideo.addObserver(this._republishObserver);
    this.audioVideo.stop();
  }

  // Remove the pending republish observer, if any. Safe to call repeatedly.
  disableRepublishObserver() {
    if (this._republishObserver) {
      this.audioVideo.removeObserver(this._republishObserver);
      this._republishObserver = null;
    }
  }

  async updateDeviceStream() {
    try {
      const oldAvailableDevices = this.availableDevices;
      const devices = await this.setDeviceAvailability();

      this.setDevicesToDefault();

      // Fallback to default camera when selected device was removed but cameras still exist
      if (!this.videoDevice && devices.cameras.length > 0 && !store.getters['devices/hasCameraError']) {
        this.videoDevice = devices.cameras[0].deviceId;
      }

      const manualMicId = getManualMicId(store.state.userId, devices.mics);

      if (manualMicId) {
        this.audioInputDevice = manualMicId;
      } else if (store.getters['uaParser/isMobileDevice']) {
        this.audioInputDevice = await store.dispatch(`devices/selectExternalMic`);
      } else if (oldAvailableDevices.mics.length !== devices.mics.length) {
        // activate default device on PC once becomes available
        const defaultMic = devices.mics.find(key => key.deviceId === 'default')?.label.replace('Default - ', '');
        this.audioInputDevice = devices.mics.find(key => key.label === defaultMic)?.deviceId ?? null;
      }

      const stream = await this.republishStream();

      return stream;
    } catch (error) {
      return { error };
    }
  }

  async setVideoEffect(effect) {
    if (!await BackgroundBlurVideoFrameProcessor.isSupported()) {
      await this.audioVideo.startVideoInput(this.videoDevice);
      logger.info('Browser is not supported.');
    }

    this.videoEffect = effect;

    try {
      const processor = await BackgroundBlurVideoFrameProcessor.create(undefined, { blurStrength: 25, logger: this.logger });
      
      if (processor && effect === 'blur') {
        const chosenVideoTransformDevice = new DefaultVideoTransformDevice(this.logger, this.videoDevice, [processor]);
        await this.audioVideo.startVideoInput(chosenVideoTransformDevice);
      } else {
        await this.audioVideo.startVideoInput(this.videoDevice);
      }

      return {
        stream: this.stream
      }
    } catch (error) {
      logger.info(
        '[awschime] Failed to set blur on background.',
        { operation: 'webrtc:setVideoEffect', reason: error.message || error }
      );
    }
  }

  async republishStream() {
    try {
      this.constraints = this.videoDevice ?
        Object.assign(this.constraints, { video: Object.assign(this.videoProfile, { deviceId: this.videoDevice }) }) :
        this.constraints;
      this.constraints = this.audioInputDevice ?
        Object.assign(this.constraints, { audio: { deviceId: this.audioInputDevice } }) :
        this.constraints;

      logger.info(`New Device Constraints: ${JSON.stringify(this.constraints)}`);

      const localStream = await navigator.mediaDevices.getUserMedia(this.constraints);

      this.stream.getTracks().forEach(track => track.stop());

      if (localStream) {
        this.stream = localStream;
        this.setStreamConfig();
      }

      // Set up devices
      if (this.audioInputDevice) await this.setAudioDevice(this.audioInputDevice);
      await this.setVideoDevice(this.videoDevice);

      // Start sending video
      this.audioVideo.startLocalVideoTile();
      this.audioVideo.start();

      return {
        stream: this.stream,
        mode: true
      };
    } catch (error) {
      logger.error('[awschime] Error on republishStream', { operation: 'webrtc:republishStream', payload: error.message || error,event_type:77,lesson_id:this.lessonId });
      return { error };
    }
  }

  getPeerStream() {
    const peerStream = new MediaStream();
    const audioElement = document.getElementById('meeting-audio');
    if (audioElement && audioElement.srcObject) {
      const remoteAudioTrack = audioElement.srcObject.getTracks()[0];
      peerStream.addTrack(remoteAudioTrack);
    }
    return peerStream;
  }

  // The SDK reconnect closes the old peer connection, ending remote tracks without firing 'ended',
  // so remote tiles stay bound to the dead stream. The SDK then skips the new remote track because
  // a tile for that attendee "already has a stream", leaving a blank tile. Drop them so fresh tiles are created.
  removeStaleRemoteTiles() {
    this.audioVideo.getAllRemoteVideoTiles().forEach(tile => {
      const tileId = tile.state().tileId;
      this.audioVideo.unpauseVideoTile(tileId);
      this.audioVideo.removeVideoTile(tileId);
    });
    logger.info('[AWS Chime] Removed stale remote video tiles before reconnect', { operation: 'webrtc:connection', lesson_id: this.lessonId });
  }

  // After an auto-reconnect, undo the audio-only fallback, make sure the local camera
  // is published again and rebind tiles to the (possibly re-rendered) video elements.
  restoreVideoAfterReconnect() {
    this.wasReconnecting = false;
    this.reconnectedAt = Date.now();
    logger.info('[AWS Chime] Session reconnected, restoring video tiles', { operation: 'webrtc:connection', lesson_id: this.lessonId });

    this.audioVideo.getAllRemoteVideoTiles().forEach(tile => {
      this.audioVideo.unpauseVideoTile(tile.state().tileId);
    });
    store.commit('videoRtc/setAudioOnly', false);

    if (this.enableVideo && !this.isScreenShare && !this.audioVideo.hasStartedLocalVideoTile()) {
      this.audioVideo.startLocalVideoTile();
    }

    this.updateTiles();
  }

  rebindVideoTile() {
    this.audioVideo.removeLocalVideoTile();
    this.audioVideo.startLocalVideoTile();
  }

  //filter valid devices
  filterDevice(kind, device) {
    // [Safari] newly plugged in media device id is empty and permission denied on camera won't work
    const isBrowserSafari = new UAParser().getBrowser().name.includes('Safari');
    return device.kind === kind && (isBrowserSafari || device.deviceId);
  }

  setStreamConfig() {
    if (this.stream.getAudioTracks().length) {
      this.stream.getAudioTracks()[0].enabled = this.enableAudio;
    }
    if (this.stream.getVideoTracks().length) {
      this.stream.getVideoTracks()[0].enabled = this.enableVideo;
    }
  }

  setDevicesToDefault() {
    if (this.constraints.video) {
      this.constraints.video.deviceId = undefined;
    }

    if (this.configuration.audio) {
      this.constraints.audio.deviceId = undefined;
    }
  }

  async setBlurWhenInActiveState() {
    if (this.videoEffect === 'blur') {
      return await this.setVideoEffect(this.videoEffect, null);
    }
  }

  devicesListHasCurrentDeviceId(cameras, mics) {
    this.videoDevice = this.videoDevice && cameras.map(device => device.deviceId).includes(this.videoDevice) ? this.videoDevice : null;
    this.audioInputDevice = this.audioInputDevice && mics.map(device => device.deviceId).includes(this.audioInputDevice) ? this.audioInputDevice : null;

    this.constraints.video = cameras.length > 0 && !store.getters['devices/hasCameraError'] ? {
      deviceId: this.videoDevice ?? undefined,
    } : false;

    this.constraints.audio = this.audioInputDevice && !store.getters['devices/hasMicError'] ? { deviceId: this.audioInputDevice ?? undefined } : false;
  }

  isRemoteUserExist(peerId) {
    return this.remoteUsers.filter(item => item.peerId == peerId).length > 0
  }

  checkUserDeviceIsMobile() {
    const uaDataIsMobile = this.isAppleDevice() || window.navigator.userAgentData?.mobile;

    const isUserMobile = typeof uaDataIsMobile === 'boolean'
      ? uaDataIsMobile
      : false;

    return isUserMobile;
  }

  isAppleDevice() {
    const appleDevices = ['iPhone', 'iPad'];

    return appleDevices.map(appleDevice => window.navigator.userAgent.indexOf(appleDevice) > 0).includes(true);
  }

  setStreamToDefault() {
    return this.constraints = { 
      audio: true,
      video: true,
    };
  }

  // enable/disable camera
  async toggleCamera(state) {
    // Screen share guard — only update state flag, skip all SDK/track operations
    if (this.isScreenShare) {
      this.enableVideo = state;
      return { success: true };
    }

    if (state === false) {
      // Release camera
      try {
        this.audioVideo.stopLocalVideoTile();
      } catch (e) {
        logger.error('[AWS Chime] Error stopping local video tile', {
          operation: 'webrtc:stopVideoInput',
          error: e.message || e
        });
      }

      try {
        await this.audioVideo.stopVideoInput();
      } catch (e) {
        logger.error('[AWS Chime] Error stopping video input', {
          operation: 'webrtc:stopVideoInput',
          error: e.message || e
        });
      }

      this._releaseVideoTracks();
      this.enableVideo = false;

      logger.info('[AWS Chime] Camera released', { operation: 'webrtc:stopVideoInput' });
      return { success: true };
    }

    // state === true — Acquire camera
    try {
      this.enableVideo = true;

      // Handle blur transform device
      let device = this.videoDevice;
      if (this.videoEffect === 'blur') {
        const processor = await BackgroundBlurVideoFrameProcessor.create(undefined, { blurStrength: 25, logger: this.logger });
        device = new DefaultVideoTransformDevice(this.logger, this.videoDevice, [processor]);
      }

      await this.audioVideo.startVideoInput(device);
      this.audioVideo.startLocalVideoTile();

      logger.info('[AWS Chime] Camera acquired', { operation: 'webrtc:startVideoInput' });
      return { success: true };
    } catch (e) {
      this.enableVideo = false;
      logger.error('[AWS Chime] Error starting video input', {
        operation: 'webrtc:startVideoInput',
        error: e.message || e
      });
      return { success: false, error: e.message || e };
    }
  }

  // enable/disable microphone
  toggleMic(state) {
    this.enableAudio = state;
    if (this.stream && this.stream.getAudioTracks().length > 0) {
      this.stream.getAudioTracks().forEach(function(track) {
        track.enabled = state;
      });
    }
    state ? this.audioVideo.realtimeUnmuteLocalAudio() : this.audioVideo.realtimeMuteLocalAudio();
  }

  toggleRemoteVideo(pauseVideo) {
    const remoteVideoTile = this.getRemoteVideoTile();
    if (remoteVideoTile) {
      store.commit('videoRtc/setAudioOnly', pauseVideo);
      const tileId = remoteVideoTile.state().tileId;
      if (pauseVideo) {
        this.audioVideo.pauseVideoTile(tileId);
      } else {
        this.audioVideo.unpauseVideoTile(tileId);
      }
    }
  }

  getRemoteVideoTile() {
    const remoteVideoTiles = this.audioVideo.getAllRemoteVideoTiles();
    return remoteVideoTiles.length > 0 ? remoteVideoTiles[0] : null;
  }

  _releaseVideoTracks() {
    if (!this.stream) return;
    this.stream.getVideoTracks().forEach(track => {
      try {
        track.stop();
      } catch (e) {
        logger.error('[AWS Chime] Error stopping video track', {
          operation: 'webrtc:stopVideoInput',
          error: e.message || e
        });
      }
    });
  }

  _releaseAllTracks() {
    if (!this.stream) return;
    this.stream.getTracks().forEach(track => {
      try {
        track.stop();
      } catch (e) {
        logger.error('[AWS Chime] Error stopping track', {
          operation: 'webrtc:releaseTrack',
          error: e.message || e
        });
      }
    });
  }

  async disconnect() {
    window.removeEventListener('offline', this._onOffline);
    window.removeEventListener('online', this._onOnline);
    this.stopReconnectRecoveryPoll();

    // Disable any pending screen-share restart so it does not fire on the stop()
    // below and restart the meeting after the user has left.
    this.disableRepublishObserver();

    // Stop screen share if still active so it ends with the lesson.
    if (this.isScreenShare) {
      try {
        this.audioVideo.stopContentShare();
      } catch (e) {
        logger.error('[AWS Chime] Error stopping content share during disconnect', {
          operation: 'webrtc:stopContentShare',
          error: e.message || e
        });
      }
      this.isScreenShare = false;
      store.commit('videoRtc/setIsShareScreenOn', false);
    }

    try {
      await this.audioVideo.stopVideoInput();
    } catch (e) {
      logger.error('[AWS Chime] Error stopping video input during disconnect', {
        operation: 'webrtc:stopVideoInput',
        error: e.message || e
      });
    }

    this._releaseAllTracks();
    this.stream = null;

    this.audioVideo.stop();
  }
}

export default ChimeAdapter;
